<template>
  <v-container class="inv-admin">
    <v-layout row wrap>
      <v-flex xs12 md11>
        <h1>Festival inventory</h1>
        <p class="lede">
          Shared across every festival year — bins, loaner Becoming pieces, and tutus.
          Year switcher does not change this list.
        </p>

        <p v-if="loadError" class="error-banner" role="alert">{{ loadError }}</p>
        <p v-if="saveError" class="error-banner" role="alert">{{ saveError }}</p>
        <p v-if="okMsg" class="ok-banner" role="status">{{ okMsg }}</p>

        <div class="tabs" role="tablist">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            class="tab"
            :class="{ 'is-active': activeTab === tab.id }"
            @click="activeTab = tab.id"
          >
            {{ tab.label }}
            <span class="tab-count">{{ tab.count }}</span>
          </button>
        </div>

        <!-- ═══ SUPPLIES ═══ -->
        <section v-if="activeTab === 'supply'">
          <div class="toolbar">
            <input
              v-model.trim="search"
              class="search"
              type="search"
              placeholder="Search supplies"
            >
            <button type="button" class="btn" @click="openAddSupply()">+ Add item</button>
            <router-link class="btn" to="/iabf/admin/inventory/print">Print packing sheets</router-link>
            <button
              v-if="!hasSeededSupplies"
              type="button"
              class="btn primary"
              :disabled="seeding"
              @click="seedSupplies"
            >
              {{ seeding ? 'Loading notes…' : 'Load packing notes' }}
            </button>
          </div>

          <div v-if="loading" class="muted">Loading…</div>
          <p v-else-if="search && !visibleBoxes.length" class="muted">No supplies match “{{ search }}”.</p>

          <section
            v-for="box in visibleBoxes"
            :key="box.id"
            class="panel"
          >
            <div class="panel-head">
              <h2>{{ boxLabel(box.id) }}</h2>
              <button
                v-if="box.id !== closetBoxId"
                type="button"
                class="btn-link"
                @click="openAddSupply(box.id)"
              >+ Add to this box</button>
            </div>
            <p v-if="box.id === closetBoxId" class="hint empty-box">
              Live from the costume closet. Edit pieces on the Closet tab.
            </p>
            <p v-if="!itemsForBox(box.id).length && !costumesInBox(box.id).length && !closetRowsForBox(box.id).length" class="muted empty-box">
              No items listed yet.
            </p>
            <table v-if="closetRowsForBox(box.id).length" class="inv-table">
              <thead>
                <tr>
                  <th class="qty-col">Qty</th>
                  <th>Item</th>
                  <th>Kind</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in closetRowsForBox(box.id)" :key="row.key">
                  <td class="qty-col">{{ formatQty(row.qty) }}</td>
                  <td>
                    {{ row.name }}
                    <span v-if="row.notes" class="muted"> {{ row.notes }}</span>
                  </td>
                  <td class="muted">{{ row.categoryLabel }}</td>
                </tr>
              </tbody>
            </table>
            <table v-if="itemsForBox(box.id).length" class="inv-table">
              <thead>
                <tr>
                  <th class="qty-col">Qty</th>
                  <th>Item</th>
                  <th>Notes</th>
                  <th class="act-col"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in itemsForBox(box.id)" :key="row.id">
                  <td class="qty-col">{{ formatQty(row.qty) }}</td>
                  <td>{{ row.name }}</td>
                  <td class="muted">{{ row.notes || '—' }}</td>
                  <td class="act-col">
                    <button type="button" class="btn-link" @click="openEdit(row)">Edit</button>
                    <button type="button" class="btn-link danger" @click="removeItem(row)">Delete</button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="costumesInBox(box.id).length" class="box-costumes">
              <h3 class="box-costumes-title">Costumes in this box</h3>
              <ul class="box-costume-list">
                <li v-for="c in costumesInBox(box.id)" :key="c.key">{{ c.label }}</li>
              </ul>
            </div>
          </section>
        </section>

        <!-- ═══ BECOMING ═══ -->
        <section v-else-if="activeTab === 'becoming'">
          <div class="toolbar">
            <button type="button" class="btn" @click="openAddBecoming()">+ Add piece</button>
          </div>
          <p class="hint">
            Loaner Becoming stock. After you clean a size lot, check it in and pick the tub it lives in.
          </p>

          <div class="panel">
            <h2>On hand</h2>
            <p v-if="!becomingItems.length" class="muted">
              No Becoming pieces yet. Add pieces by size.
            </p>
            <table v-else class="inv-table">
              <thead>
                <tr>
                  <th>Piece</th>
                  <th>Size</th>
                  <th class="qty-col">Qty</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th class="act-col"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in becomingItems" :key="row.id">
                  <td>{{ becomingKindLabel(row.becomingKind) }}</td>
                  <td>{{ row.size || '—' }}</td>
                  <td class="qty-col">{{ formatQty(row.qty) }}</td>
                  <td>
                    <span v-if="isCostumeCheckedIn(row)" class="status-in">In · {{ boxLabel(row.storageBox) }}</span>
                    <span v-else class="status-out">Not checked in</span>
                  </td>
                  <td class="muted">{{ row.notes || '—' }}</td>
                  <td class="act-col">
                    <button type="button" class="btn-link" @click="openCheckIn(row, 'becoming')">
                      {{ isCostumeCheckedIn(row) ? 'Move' : 'Check in' }}
                    </button>
                    <button type="button" class="btn-link" @click="openEdit(row)">Edit</button>
                    <button type="button" class="btn-link danger" @click="removeItem(row)">Delete</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- ═══ TUTUS ═══ -->
        <section v-else-if="activeTab === 'tutu'">
          <div class="toolbar">
            <input
              v-model.trim="tutuSearch"
              class="search"
              type="search"
              placeholder="Search tutus"
            >
            <div class="filter-pills">
              <button
                type="button"
                class="pill"
                :class="{ 'is-on': tutuFilter === 'all' }"
                @click="tutuFilter = 'all'"
              >All</button>
              <button
                type="button"
                class="pill"
                :class="{ 'is-on': tutuFilter === 'out' }"
                @click="tutuFilter = 'out'"
              >Not in ({{ tutusNotInCount }})</button>
              <button
                type="button"
                class="pill"
                :class="{ 'is-on': tutuFilter === 'in' }"
                @click="tutuFilter = 'in'"
              >Checked in</button>
            </div>
            <router-link class="btn" to="/iabf/admin/costume-inventory">Manage tutu inventory</router-link>
          </div>
          <p class="hint">
            After you clean a tutu, check it in and pick the tub (usually boxes 9–12).
            Measurements are shown in cm to match the tags.
          </p>

          <div class="panel">
            <h2>Tutu stock ({{ filteredTutus.length }})</h2>
            <p v-if="!tutus.length" class="muted">No tutus in costume inventory yet.</p>
            <table v-else class="inv-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Color</th>
                  <th>Measurements (cm)</th>
                  <th>Last borrowed (2026)</th>
                  <th>Status</th>
                  <th class="act-col"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in filteredTutus" :key="row.id">
                  <td>{{ row.name }}</td>
                  <td>{{ row.type || '—' }}</td>
                  <td>{{ row.color || '—' }}</td>
                  <td class="meas-cell">{{ formatTutuMeasurementsCm(row) }}</td>
                  <td>
                    <span v-if="row.lastBorrower">{{ row.lastBorrower }} <span class="muted">{{ row.lastRole }}</span></span>
                    <span v-else class="muted">—</span>
                  </td>
                  <td>
                    <span v-if="isCostumeCheckedIn(row)" class="status-in">In · {{ boxLabel(row.storageBox) }}</span>
                    <span v-else class="status-out">Not checked in</span>
                  </td>
                  <td class="act-col">
                    <button type="button" class="btn-link" @click="openCheckIn(row, 'tutu')">
                      {{ isCostumeCheckedIn(row) ? 'Move' : 'Check in' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- ═══ COSTUME CLOSET ═══ -->
        <section v-else-if="activeTab === 'closet'">
          <div class="toolbar">
            <input
              v-model.trim="closetSearch"
              class="search"
              type="search"
              placeholder="Search closet"
            >
            <button type="button" class="btn" @click="openAddCloset()">+ Add item</button>
            <button
              v-if="!hasSeededCloset"
              type="button"
              class="btn primary"
              :disabled="seedingCloset"
              @click="seedCloset"
            >
              {{ seedingCloset ? 'Loading list…' : 'Load closet list' }}
            </button>
          </div>
          <p class="hint">
            Pieces dancers can borrow. Add a photo and tidy the description whenever you have a minute.
          </p>
          <div class="panel">
            <p v-if="!closetItems.length" class="muted">No closet pieces yet.</p>
            <table v-else class="inv-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Kind</th>
                  <th class="qty-col">Qty</th>
                  <th>Left</th>
                  <th>Fee</th>
                  <th>Description</th>
                  <th class="act-col"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in filteredClosetItems" :key="row.id">
                  <td>
                    <strong>{{ row.name }}</strong>
                    <span v-if="row.photoUrl" class="muted"> · photo</span>
                  </td>
                  <td>{{ closetCategoryLabel(row.category) }}</td>
                  <td class="qty-col">{{ formatQty(row.qty) }}</td>
                  <td class="qty-col">{{ closetAvailable(row) }}</td>
                  <td>{{ formatClosetPrice(closetItemPriceCents(row)) }}</td>
                  <td class="muted">{{ row.description || '—' }}</td>
                  <td class="act-col">
                    <button type="button" class="btn-link" @click="openEditCloset(row)">Edit</button>
                    <button type="button" class="btn-link danger" @click="removeClosetItem(row)">Delete</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="panel">
            <h2>Loans · {{ closetLoanYear }}</h2>
            <p v-if="!activeClosetLoans.length" class="muted">No reserved or checked-out pieces this year.</p>
            <table v-else class="inv-table">
              <thead>
                <tr>
                  <th>Dancer</th>
                  <th>Piece</th>
                  <th>Fee</th>
                  <th>Status</th>
                  <th>Ledger</th>
                  <th class="act-col"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="loan in activeClosetLoans" :key="loan.id">
                  <td>{{ loan.dancerName || loan.email }}</td>
                  <td>{{ loan.itemName }}</td>
                  <td>{{ formatClosetPrice(loan.amountCents) }}</td>
                  <td>{{ loan.status }}</td>
                  <td>{{ ledgerStatusForLoan(loan) }}</td>
                  <td class="act-col">
                    <button
                      v-if="loan.status === 'reserved'"
                      type="button"
                      class="btn-link"
                      :disabled="loanBusyId === loan.id"
                      @click="adminCheckout(loan)"
                    >Check out</button>
                    <button
                      v-if="loan.status === 'reserved' || loan.status === 'checked-out'"
                      type="button"
                      class="btn-link"
                      :disabled="loanBusyId === loan.id"
                      @click="adminReturn(loan)"
                    >Check in</button>
                    <button
                      v-if="loan.status === 'reserved'"
                      type="button"
                      class="btn-link danger"
                      :disabled="loanBusyId === loan.id"
                      @click="adminCancelLoan(loan)"
                    >Cancel</button>
                    <button
                      v-if="loanLedgerOpen(loan)"
                      type="button"
                      class="btn-link"
                      :disabled="loanBusyId === loan.id"
                      @click="adminMarkPaid(loan)"
                    >Mark paid</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Closet edit dialog -->
        <div v-if="closetDialog" class="modal-backdrop" @click.self="closeClosetDialog">
          <div class="modal" role="dialog" aria-modal="true">
            <h3>{{ closetForm.id ? 'Edit closet item' : 'Add closet item' }}</h3>
            <label class="field">
              Name
              <input v-model.trim="closetForm.name" type="text">
            </label>
            <label class="field">
              Kind
              <select v-model="closetForm.category">
                <option v-for="c in closetCategories" :key="c.id" :value="c.id">{{ c.label }}</option>
              </select>
            </label>
            <label class="field">
              Quantity
              <input v-model="closetForm.qty" type="number" min="0" step="1" placeholder="Unknown">
            </label>
            <label class="field">
              Borrow fee (USD)
              <input v-model="closetForm.priceDollars" type="number" min="0" step="1" placeholder="Default by type">
              <span class="muted">Blank = $15 tutus, $5 everything else. 0 = free.</span>
            </label>
            <label class="field">
              Description
              <textarea v-model.trim="closetForm.description" rows="3"></textarea>
            </label>
            <label class="field">
              Photo
              <input type="file" accept="image/*" @change="onClosetPhotoChosen">
              <span v-if="closetUploading" class="muted">Uploading…</span>
            </label>
            <img
              v-if="closetForm.photoUrl"
              :src="closetForm.photoUrl"
              alt=""
              class="closet-thumb"
            >
            <div class="modal-actions">
              <button type="button" class="btn" @click="closeClosetDialog">Cancel</button>
              <button
                type="button"
                class="btn primary"
                :disabled="saving || !closetForm.name"
                @click="saveClosetForm"
              >
                {{ saving ? 'Saving…' : 'Save' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Check in dialog -->
        <div v-if="checkInDialog" class="modal-backdrop" @click.self="closeCheckIn">
          <div class="modal" role="dialog" aria-modal="true">
            <h3>{{ checkInIsMove ? 'Move' : 'Check in' }}</h3>
            <p class="checkin-name">{{ checkInLabel }}</p>
            <p class="hint" style="margin-bottom: 12px">
              Cleaned and going into which tub?
            </p>
            <label class="field">
              Box
              <select v-model.number="checkInBox">
                <option v-for="b in boxes" :key="b.id" :value="b.id">{{ boxLabel(b.id) }}</option>
              </select>
            </label>
            <div class="modal-actions">
              <button type="button" class="btn" @click="closeCheckIn">Cancel</button>
              <button
                type="button"
                class="btn primary"
                :disabled="saving || !checkInBox"
                @click="confirmCheckIn"
              >
                {{ saving ? 'Saving…' : (checkInIsMove ? 'Save box' : 'Check in') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Add / Edit dialog -->
        <div v-if="dialog" class="modal-backdrop" @click.self="closeDialog">
          <div class="modal" role="dialog" aria-modal="true">
            <h3>{{ editingId ? 'Edit item' : 'Add item' }}</h3>
            <label v-if="form.category === 'supply'" class="field">
              Box
              <select v-model.number="form.box">
                <option v-for="b in boxes" :key="b.id" :value="b.id">{{ boxLabel(b.id) }}</option>
              </select>
            </label>
            <label v-if="form.category === 'becoming'" class="field">
              Piece
              <select v-model="form.becomingKind">
                <option v-for="k in becomingKinds" :key="k.id" :value="k.id">{{ k.label }}</option>
              </select>
            </label>
            <label v-if="form.category === 'becoming'" class="field">
              Size
              <select v-model="form.size">
                <option v-for="s in becomingSizes" :key="s" :value="s">{{ s }}</option>
              </select>
            </label>
            <label v-if="form.category === 'supply'" class="field">
              Name
              <input v-model.trim="form.name" type="text">
            </label>
            <label class="field">
              Quantity
              <input v-model="form.qty" type="number" min="0" step="1" placeholder="Unknown">
            </label>
            <label class="field">
              Notes
              <input v-model.trim="form.notes" type="text">
            </label>
            <div class="modal-actions">
              <button type="button" class="btn" @click="closeDialog">Cancel</button>
              <button
                type="button"
                class="btn primary"
                :disabled="saving || !canSaveForm"
                @click="saveForm"
              >
                {{ saving ? 'Saving…' : 'Save' }}
              </button>
            </div>
          </div>
        </div>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { db, storage } from '@/firebase'
import { dancersCollection, IABF_DANCER_YEAR } from '@/config/iabf-event'
import {
  CLOSET_SEED_ITEMS,
  CLOSET_CATEGORIES,
  CLOSET_STORAGE_PREFIX,
  CLOSET_LOAN_ACTIVE,
  closetCategoryLabel,
  closetItemPriceCents,
  formatClosetPrice,
  closetBorrowNote
} from '@/config/iabf-costume-closet'
import {
  INVENTORY_BOXES,
  BECOMING_SIZES,
  BECOMING_KINDS,
  SUPPLY_SEED_ITEMS,
  TUTU_BOX_IDS,
  CLOSET_BOX_ID,
  boxLabel,
  becomingItemName,
  isCostumeCheckedIn
} from '@/config/iabf-inventory'

function emptyClosetForm () {
  return {
    id: null,
    name: '',
    category: 'character',
    qty: '',
    priceDollars: '',
    description: '',
    photoUrl: ''
  }
}

function emptyForm (category, box) {
  return {
    category: category || 'supply',
    box: box || 1,
    name: '',
    qty: '',
    notes: '',
    becomingKind: 'skirt',
    size: 'MA'
  }
}

export default {
  name: 'FestivalInventory',
  data () {
    return {
      activeTab: 'supply',
      search: '',
      tutuSearch: '',
      tutuFilter: 'out',
      checkInDialog: false,
      checkInKind: 'tutu',
      checkInItem: null,
      checkInBox: TUTU_BOX_IDS[0],
      lastCheckInBox: TUTU_BOX_IDS[0],
      loading: true,
      loadError: '',
      saveError: '',
      okMsg: '',
      seeding: false,
      saving: false,
      dialog: false,
      editingId: null,
      form: emptyForm(),
      dancers2026: [],
      boxes: INVENTORY_BOXES,
      becomingSizes: BECOMING_SIZES,
      becomingKinds: BECOMING_KINDS,
      closetSearch: '',
      closetDialog: false,
      closetUploading: false,
      seedingCloset: false,
      closetCategories: CLOSET_CATEGORIES,
      closetForm: emptyClosetForm(),
      loanBusyId: '',
      closetBoxId: CLOSET_BOX_ID
    }
  },
  computed: {
    items () {
      return this.$store.getters.festivalInventory || []
    },
    tutus () {
      return this.$store.getters.costumeInventory || []
    },
    supplyItems () {
      const q = this.search.toLowerCase()
      return this.items.filter(i => {
        if (i.category !== 'supply') {
          return false
        }
        if (!q) {
          return true
        }
        const hay = [i.name, i.notes, boxLabel(i.box)].join(' ').toLowerCase()
        return hay.indexOf(q) !== -1
      })
    },
    becomingItems () {
      const kindOrder = { skirt: 0, crop: 1 }
      const sizeOrder = {}
      BECOMING_SIZES.forEach((s, i) => {
        sizeOrder[s] = i
      })
      return this.items.filter(i => i.category === 'becoming').slice().sort((a, b) => {
        const k = (kindOrder[a.becomingKind] || 0) - (kindOrder[b.becomingKind] || 0)
        if (k !== 0) {
          return k
        }
        return (sizeOrder[a.size] || 0) - (sizeOrder[b.size] || 0)
      })
    },
    hasSeededSupplies () {
      return this.items.some(i => i.category === 'supply')
    },
    visibleBoxes () {
      if (!this.search) {
        return this.boxes
      }
      return this.boxes.filter(box => {
        return this.itemsForBox(box.id).length > 0 ||
          this.costumesInBox(box.id).length > 0 ||
          this.closetRowsForBox(box.id).length > 0
      })
    },
    lastBorrowerByTutu () {
      const map = new Map()
      const add = (tutuName, dancer, role) => {
        if (!tutuName) {
          return
        }
        const name = ((dancer.firstName || '') + ' ' + (dancer.lastName ? dancer.lastName.charAt(0) + '.' : '')).trim()
        map.set(tutuName, { name, role })
      }
      this.dancers2026.forEach(d => {
        add(d.borrowedWaltzTutu, d, 'Waltz of the Hours')
        add(d.borrowedAliceTutu, d, 'Alice in Wonderland')
        add(d.borrowedGarlandTutu, d, 'Garland Waltz')
      })
      return map
    },
    filteredTutus () {
      const q = this.tutuSearch.toLowerCase()
      return this.tutus
        .map(t => {
          const last = this.lastBorrowerByTutu.get(t.name)
          return {
            ...t,
            lastBorrower: last ? last.name : '',
            lastRole: last ? last.role : ''
          }
        })
        .filter(t => {
          if (this.tutuFilter === 'in' && !isCostumeCheckedIn(t)) {
            return false
          }
          if (this.tutuFilter === 'out' && isCostumeCheckedIn(t)) {
            return false
          }
          if (!q) {
            return true
          }
          const hay = [t.name, t.type, t.color, t.lastBorrower, t.lastRole, t.storageBox, this.formatTutuMeasurementsCm(t)].join(' ').toLowerCase()
          return hay.indexOf(q) !== -1
        })
    },
    tutusNotInCount () {
      return this.tutus.filter(t => !isCostumeCheckedIn(t)).length
    },
    checkInIsMove () {
      return isCostumeCheckedIn(this.checkInItem)
    },
    checkInLabel () {
      const item = this.checkInItem
      if (!item) {
        return ''
      }
      if (this.checkInKind === 'becoming') {
        const qty = item.qty != null ? (' × ' + item.qty) : ''
        return becomingItemName(item.becomingKind, item.size) + qty
      }
      return item.name || 'Costume'
    },
    closetItems () {
      return this.$store.getters.costumeCloset || []
    },
    filteredClosetItems () {
      const q = this.closetSearch.toLowerCase()
      const catOrder = {}
      CLOSET_CATEGORIES.forEach((c, i) => {
        catOrder[c.id] = i
      })
      return this.closetItems
        .slice()
        .sort((a, b) => {
          const c = (catOrder[a.category] || 99) - (catOrder[b.category] || 99)
          if (c !== 0) {
            return c
          }
          return String(a.name || '').localeCompare(String(b.name || ''))
        })
        .filter(row => {
          if (!q) {
            return true
          }
          const hay = [row.name, row.description, closetCategoryLabel(row.category)].join(' ').toLowerCase()
          return hay.indexOf(q) !== -1
        })
    },
    hasSeededCloset () {
      return this.closetItems.length > 0
    },
    closetLoanYear () {
      return Number(this.$store.getters.adminFestivalYear) || IABF_DANCER_YEAR
    },
    closetLoans () {
      return this.$store.getters.closetLoans || []
    },
    closetLedger () {
      return this.$store.getters.dancerLedger || []
    },
    activeClosetLoans () {
      return this.closetLoans
        .filter(l => CLOSET_LOAN_ACTIVE.indexOf(l.status) !== -1)
        .slice()
        .sort((a, b) => String(a.itemName || '').localeCompare(String(b.itemName || '')))
    },
    tabs () {
      return [
        { id: 'supply', label: 'Supplies', count: this.items.filter(i => i.category === 'supply').length },
        { id: 'becoming', label: 'Becoming', count: this.becomingItems.length },
        { id: 'tutu', label: 'Tutus', count: this.tutus.length },
        { id: 'closet', label: 'Closet', count: this.closetItems.length }
      ]
    },
    canSaveForm () {
      if (this.form.category === 'becoming') {
        return !!(this.form.becomingKind && this.form.size)
      }
      return !!this.form.name
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
    this.bootstrap()
  },
  methods: {
    boxLabel,
    isCostumeCheckedIn,
    closetCategoryLabel,
    closetItemPriceCents,
    formatClosetPrice,
    becomingKindLabel (kind) {
      const k = BECOMING_KINDS.find(x => x.id === kind)
      return k ? k.label : (kind || 'Becoming')
    },
    inchesToCm (inches) {
      const n = Number(inches)
      if (!Number.isFinite(n) || n === 0) {
        return ''
      }
      const cm = n * 2.54
      const rounded = Math.round(cm * 10) / 10
      return Number.isInteger(rounded) ? String(rounded) : String(rounded)
    },
    formatTutuMeasurementsCm (row) {
      const type = String(row.type || '')
      const withBodice = type === 'Pancake with Bodice'
      const fields = withBodice
        ? [
          { label: 'Ht', val: row.height },
          { label: 'W', val: row.waist },
          { label: 'H', val: row.hip },
          { label: 'B', val: row.bust },
          { label: '½G', val: row.halfGirth },
          { label: 'G', val: row.girth }
        ]
        : [
          { label: 'Ht', val: row.height },
          { label: 'W', val: row.waist },
          { label: 'H', val: row.hip }
        ]
      const parts = fields
        .map(p => {
          const cm = this.inchesToCm(p.val)
          return cm ? (p.label + ' ' + cm) : ''
        })
        .filter(Boolean)
      return parts.length ? parts.join(' · ') : '—'
    },
    formatQty (qty) {
      if (qty === 0) {
        return '0'
      }
      if (qty == null || qty === '') {
        return '—'
      }
      return String(qty)
    },
    itemsForBox (boxId) {
      return this.supplyItems
        .filter(i => Number(i.box) === Number(boxId))
        .slice()
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    },
    closetRowsForBox (boxId) {
      if (Number(boxId) !== CLOSET_BOX_ID) {
        return []
      }
      const q = (this.search || '').toLowerCase()
      const year = this.closetLoanYear
      return this.closetItems
        .slice()
        .sort((a, b) => {
          const cat = closetCategoryLabel(a.category).localeCompare(closetCategoryLabel(b.category))
          if (cat !== 0) {
            return cat
          }
          return String(a.name || '').localeCompare(String(b.name || ''))
        })
        .map(item => {
          const notes = closetBorrowNote(item, this.closetLoans, year)
          return {
            key: 'closet-' + item.id,
            name: item.name || '',
            qty: item.qty,
            notes,
            categoryLabel: closetCategoryLabel(item.category)
          }
        })
        .filter(row => {
          if (!q) {
            return true
          }
          const hay = [row.name, row.notes, row.categoryLabel].join(' ').toLowerCase()
          return hay.indexOf(q) !== -1
        })
    },
    costumesInBox (boxId) {
      const n = Number(boxId)
      const q = (this.search || '').toLowerCase()
      const matches = (label) => !q || label.toLowerCase().indexOf(q) !== -1
      const tutus = this.tutus
        .filter(t => isCostumeCheckedIn(t) && Number(t.storageBox) === n)
        .map(t => ({
          key: 'tutu-' + t.id,
          label: t.name + (t.color ? (' · ' + t.color) : '')
        }))
        .filter(c => matches(c.label))
      const becoming = this.becomingItems
        .filter(b => isCostumeCheckedIn(b) && Number(b.storageBox) === n)
        .map(b => ({
          key: 'bec-' + b.id,
          label: becomingItemName(b.becomingKind, b.size) + (b.qty != null ? (' × ' + b.qty) : '')
        }))
        .filter(c => matches(c.label))
      return tutus.concat(becoming)
    },
    openCheckIn (item, kind) {
      this.checkInKind = kind
      this.checkInItem = item
      const existing = Number(item.storageBox)
      this.checkInBox = existing || this.lastCheckInBox || TUTU_BOX_IDS[0]
      this.checkInDialog = true
    },
    closeCheckIn () {
      this.checkInDialog = false
      this.checkInItem = null
    },
    async confirmCheckIn () {
      if (!this.checkInItem || !this.checkInBox) {
        return
      }
      this.saving = true
      this.saveError = ''
      const box = Number(this.checkInBox)
      const payload = {
        storageStatus: 'in',
        storageBox: box,
        checkedInAt: Date.now()
      }
      try {
        if (this.checkInKind === 'tutu') {
          await this.$store.dispatch('saveCostumeInventoryItem', {
            id: this.checkInItem.id,
            ...payload
          })
        } else {
          await this.$store.dispatch('saveFestivalInventoryItem', {
            ...this.checkInItem,
            ...payload
          })
        }
        this.lastCheckInBox = box
        this.okMsg = (this.checkInItem.name || this.checkInLabel) + ' → ' + boxLabel(box)
        this.closeCheckIn()
      } catch (e) {
        this.saveError = (e && e.message) || 'Could not check in.'
      }
      this.saving = false
    },
    parseQty (raw) {
      if (raw === '' || raw == null) {
        return null
      }
      const n = Number(raw)
      return Number.isFinite(n) && n >= 0 ? n : null
    },
    async bootstrap () {
      this.loading = true
      this.loadError = ''
      try {
        await Promise.all([
          this.$store.dispatch('loadFestivalInventory'),
          this.$store.dispatch('loadCostumeInventory'),
          this.$store.dispatch('loadCostumeCloset'),
          this.$store.dispatch('loadClosetLoans', this.closetLoanYear),
          this.$store.dispatch('loadDancerLedger', { year: this.closetLoanYear }),
          this.loadDancers2026()
        ])
        await this.correctMovedSeedItems()
      } catch (e) {
        this.loadError = (e && e.message) || 'Could not load inventory.'
      }
      this.loading = false
    },
    async loadDancers2026 () {
      const colName = dancersCollection(2026)
      const snap = await db.collection(colName).get()
      this.dancers2026 = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    },
    openAddSupply (box) {
      this.editingId = null
      this.form = emptyForm('supply', box || 1)
      this.dialog = true
    },
    openAddBecoming () {
      this.editingId = null
      this.form = emptyForm('becoming')
      this.dialog = true
    },
    openEdit (row) {
      this.editingId = row.id
      this.form = {
        category: row.category,
        box: Number(row.box) || 1,
        name: row.name || '',
        qty: row.qty == null ? '' : row.qty,
        notes: row.notes || '',
        becomingKind: row.becomingKind || 'skirt',
        size: row.size || 'MA'
      }
      this.dialog = true
    },
    closeDialog () {
      this.dialog = false
      this.editingId = null
    },
    async saveForm () {
      this.saving = true
      this.saveError = ''
      try {
        const qty = this.parseQty(this.form.qty)
        const payload = {
          id: this.editingId || undefined,
          category: this.form.category,
          qty,
          notes: this.form.notes || '',
          source: 'manual'
        }
        if (this.form.category === 'becoming') {
          payload.becomingKind = this.form.becomingKind
          payload.size = this.form.size
          payload.name = becomingItemName(this.form.becomingKind, this.form.size)
          payload.box = null
        } else {
          payload.box = Number(this.form.box) || 1
          payload.name = this.form.name
        }
        await this.$store.dispatch('saveFestivalInventoryItem', payload)
        this.okMsg = 'Saved.'
        this.closeDialog()
      } catch (e) {
        this.saveError = (e && e.message) || 'Could not save.'
      }
      this.saving = false
    },
    async removeItem (row) {
      if (!window.confirm('Delete “' + row.name + '”?')) {
        return
      }
      this.saveError = ''
      try {
        await this.$store.dispatch('deleteFestivalInventoryItem', row.id)
        this.okMsg = 'Deleted.'
      } catch (e) {
        this.saveError = (e && e.message) || 'Could not delete.'
      }
    },
    /**
     * If packing notes were loaded before the box-3 / box-15 corrections,
     * move/rename those rows in place (idempotent).
     */
    async correctMovedSeedItems () {
      const supplies = this.items.filter(i => i.category === 'supply')
      if (!supplies.length) {
        return
      }
      const byName = (name) => supplies.find(i => i.name === name)
      const cablesOld = byName('Bag of cases & tots')
      const cablesNew = byName('Bag of cables and tools')
      const blocks = byName('Bag of extra floor blocks')
      const scissors = byName('Scissors')
      const jobs = []
      if (cablesOld && !cablesNew) {
        jobs.push(this.$store.dispatch('saveFestivalInventoryItem', {
          ...cablesOld,
          name: 'Bag of cables and tools',
          box: 15,
          notes: 'Moved from box 3'
        }))
      }
      if (blocks && Number(blocks.box) !== 15) {
        jobs.push(this.$store.dispatch('saveFestivalInventoryItem', {
          ...blocks,
          box: 15,
          notes: 'Moved from box 3'
        }))
      }
      if (!scissors) {
        jobs.push(this.$store.dispatch('saveFestivalInventoryItem', {
          category: 'supply',
          box: 15,
          name: 'Scissors',
          qty: 1,
          notes: '',
          source: 'packing-notes-2026'
        }))
      }
      if (jobs.length) {
        await Promise.all(jobs)
      }
    },
    async seedSupplies () {
      if (this.hasSeededSupplies) {
        return
      }
      this.seeding = true
      this.saveError = ''
      try {
        const rows = SUPPLY_SEED_ITEMS.map(item => ({
          category: 'supply',
          box: item.box,
          name: item.name,
          qty: item.qty == null ? null : item.qty,
          notes: item.notes || '',
          source: 'packing-notes-2026'
        }))
        await this.$store.dispatch('bulkAddFestivalInventoryItems', rows)
        this.okMsg = 'Loaded ' + rows.length + ' items from the packing notes.'
      } catch (e) {
        this.saveError = (e && e.message) || 'Could not load packing notes.'
      }
      this.seeding = false
    },
    closetAvailable (item) {
      const stock = item.qty == null ? null : Number(item.qty)
      if (stock == null || !Number.isFinite(stock)) {
        return '—'
      }
      const used = this.closetLoans
        .filter(l => l.itemId === item.id && CLOSET_LOAN_ACTIVE.indexOf(l.status) !== -1)
        .reduce((sum, l) => sum + (Number(l.qty) || 1), 0)
      return Math.max(0, stock - used)
    },
    ledgerForLoan (loan) {
      return this.closetLedger.find(r => r.id === loan.ledgerId) || null
    },
    ledgerStatusForLoan (loan) {
      const row = this.ledgerForLoan(loan)
      if (!row) {
        return '—'
      }
      return row.status || '—'
    },
    loanLedgerOpen (loan) {
      const row = this.ledgerForLoan(loan)
      return !!(row && row.status === 'open')
    },
    async refreshClosetOps () {
      await Promise.all([
        this.$store.dispatch('loadClosetLoans', this.closetLoanYear),
        this.$store.dispatch('loadDancerLedger', { year: this.closetLoanYear })
      ])
    },
    async adminCheckout (loan) {
      this.loanBusyId = loan.id
      this.saveError = ''
      try {
        await this.$store.dispatch('checkoutClosetLoan', { loanId: loan.id })
        await this.refreshClosetOps()
        this.okMsg = 'Checked out ' + loan.itemName + '.'
      } catch (e) {
        this.saveError = (e && e.message) || 'Could not check out.'
      }
      this.loanBusyId = ''
    },
    async adminReturn (loan) {
      this.loanBusyId = loan.id
      this.saveError = ''
      try {
        await this.$store.dispatch('returnClosetLoan', { loanId: loan.id })
        await this.refreshClosetOps()
        this.okMsg = 'Checked in ' + loan.itemName + '.'
      } catch (e) {
        this.saveError = (e && e.message) || 'Could not check in.'
      }
      this.loanBusyId = ''
    },
    async adminCancelLoan (loan) {
      if (!window.confirm('Cancel this reservation and void the ledger charge?')) {
        return
      }
      this.loanBusyId = loan.id
      this.saveError = ''
      try {
        await this.$store.dispatch('cancelClosetLoan', { loanId: loan.id })
        await this.refreshClosetOps()
        this.okMsg = 'Cancelled.'
      } catch (e) {
        this.saveError = (e && e.message) || 'Could not cancel.'
      }
      this.loanBusyId = ''
    },
    async adminMarkPaid (loan) {
      if (!loan.ledgerId) {
        return
      }
      this.loanBusyId = loan.id
      this.saveError = ''
      try {
        await this.$store.dispatch('markDancerLedgerPaid', { ledgerId: loan.ledgerId })
        await this.refreshClosetOps()
        this.okMsg = 'Marked paid.'
      } catch (e) {
        this.saveError = (e && e.message) || 'Could not mark paid.'
      }
      this.loanBusyId = ''
    },
    openAddCloset () {
      this.closetForm = emptyClosetForm()
      this.closetDialog = true
    },
    openEditCloset (row) {
      const cents = row.priceCents
      this.closetForm = {
        id: row.id,
        name: row.name || '',
        category: row.category || 'character',
        qty: row.qty == null ? '' : row.qty,
        priceDollars: cents == null || cents === '' ? '' : (Number(cents) / 100),
        description: row.description || '',
        photoUrl: row.photoUrl || ''
      }
      this.closetDialog = true
    },
    closeClosetDialog () {
      this.closetDialog = false
    },
    async onClosetPhotoChosen (event) {
      const file = event.target.files && event.target.files[0]
      if (!file) {
        return
      }
      this.closetUploading = true
      this.saveError = ''
      try {
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, '_')
        const path = CLOSET_STORAGE_PREFIX + '/' + Date.now() + '-' + safeName
        const ref = storage.ref(path)
        const snap = await ref.put(file, { contentType: file.type || 'image/jpeg' })
        this.closetForm.photoUrl = await snap.ref.getDownloadURL()
      } catch (e) {
        this.saveError = (e && e.message) || 'Photo upload failed.'
      }
      this.closetUploading = false
    },
    async saveClosetForm () {
      this.saving = true
      this.saveError = ''
      try {
        const priceRaw = this.closetForm.priceDollars
        const payload = {
          id: this.closetForm.id || undefined,
          name: this.closetForm.name,
          category: this.closetForm.category,
          qty: this.parseQty(this.closetForm.qty),
          description: this.closetForm.description || '',
          photoUrl: this.closetForm.photoUrl || '',
          published: true,
          source: this.closetForm.id ? 'manual' : 'manual'
        }
        if (priceRaw === '' || priceRaw == null) {
          payload.priceCents = null
        } else {
          payload.priceCents = Math.round(Number(priceRaw) * 100)
        }
        await this.$store.dispatch('saveCostumeClosetItem', payload)
        this.okMsg = 'Saved closet item.'
        this.closeClosetDialog()
      } catch (e) {
        this.saveError = (e && e.message) || 'Could not save closet item.'
      }
      this.saving = false
    },
    async removeClosetItem (row) {
      if (!window.confirm('Delete “' + row.name + '” from the closet?')) {
        return
      }
      try {
        await this.$store.dispatch('deleteCostumeClosetItem', row.id)
        this.okMsg = 'Deleted.'
      } catch (e) {
        this.saveError = (e && e.message) || 'Could not delete.'
      }
    },
    async seedCloset () {
      if (this.hasSeededCloset || this.seedingCloset) {
        return
      }
      this.seedingCloset = true
      this.saveError = ''
      try {
        await this.$store.dispatch('loadCostumeCloset')
        if ((this.$store.getters.costumeCloset || []).length) {
          this.okMsg = 'Closet already has pieces — skipped import.'
          this.seedingCloset = false
          return
        }
        const rows = CLOSET_SEED_ITEMS.map(item => ({
          ...item,
          published: true,
          photoUrl: '',
          source: 'closet-list-2026'
        }))
        await this.$store.dispatch('bulkAddCostumeClosetItems', rows)
        this.okMsg = 'Loaded ' + rows.length + ' closet pieces from the list.'
      } catch (e) {
        this.saveError = (e && e.message) || 'Could not load closet list.'
      }
      this.seedingCloset = false
    }
  }
}
</script>

<style scoped>
.inv-admin {
  max-width: 980px;
  padding-bottom: 80px;
}
h1 {
  font-family: Georgia, serif;
  font-weight: 400;
  font-size: 32px;
  margin: 8px 0 8px;
}
.lede, .hint {
  color: #5c5a63;
  font-size: 15px;
  line-height: 1.5;
  margin: 0 0 20px;
}
.hint {
  font-size: 14px;
}
.tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.tab {
  border: 1px solid #e3e0e8;
  background: #fff;
  border-radius: 999px;
  padding: 8px 14px;
  font-size: 14px;
  cursor: pointer;
}
.tab.is-active {
  background: #8a957d;
  color: #fff;
  border-color: #8a957d;
}
.tab-count {
  margin-left: 6px;
  opacity: 0.8;
  font-size: 12px;
}
.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
  align-items: center;
}
.search {
  flex: 1;
  min-width: 180px;
  border: 1px solid #e3e0e8;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
}
.panel {
  background: #fff;
  border: 1px solid #e3e0e8;
  border-radius: 12px;
  padding: 16px 18px 18px;
  margin-bottom: 16px;
}
.panel-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 8px;
}
.panel h2, .modal h3 {
  font-size: 17px;
  margin: 0 0 8px;
}
.empty-box {
  margin: 0;
}
.inv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.inv-table + .inv-table {
  margin-top: 12px;
}
.inv-table th, .inv-table td {
  text-align: left;
  padding: 7px 8px;
  border-bottom: 1px solid #eee;
  vertical-align: top;
}
.inv-table th {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #8a8792;
}
.qty-col { width: 56px; }
.meas-cell {
  font-size: 13px;
  white-space: nowrap;
}
.act-col { width: 120px; white-space: nowrap; text-align: right; }
.btn, .btn-link {
  border: 1px solid #e3e0e8;
  background: #fff;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  display: inline-block;
}
.btn.primary {
  background: #8a957d;
  border-color: #8a957d;
  color: #fff;
}
.btn-link {
  border: none;
  padding: 4px 6px;
  color: #5c5a63;
}
.btn-link.danger { color: #b54a4a; }
.muted { color: #8a8792; }
.error-banner, .ok-banner {
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 14px;
}
.error-banner { background: #f3e8e8; color: #7a3030; }
.ok-banner { background: #e8f2ea; color: #2e5a34; }
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(28, 27, 31, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 40;
  padding: 16px;
}
.modal {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  width: 100%;
  max-width: 420px;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  margin-bottom: 10px;
}
.field input, .field select {
  border: 1px solid #e3e0e8;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}
.filter-pills {
  display: flex;
  gap: 6px;
}
.pill {
  border: 1px solid #e3e0e8;
  background: #fff;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 13px;
  cursor: pointer;
}
.pill.is-on {
  background: #eef0eb;
  border-color: #8a957d;
}
.status-in {
  color: #2e5a34;
  font-size: 13px;
}
.status-out {
  color: #8a8792;
  font-size: 13px;
}
.checkin-name {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px;
}
.box-costumes {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed #e3e0e8;
}
.box-costumes-title {
  font-size: 13px;
  margin: 0 0 6px;
  color: #5c5a63;
}
.box-costume-list {
  margin: 0;
  padding-left: 18px;
  font-size: 14px;
}
.field textarea {
  border: 1px solid #e3e0e8;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
  font-family: inherit;
}
.closet-thumb {
  display: block;
  max-width: 160px;
  max-height: 160px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 10px;
}
</style>
